import { makeAutoObservable, runInAction } from 'mobx'
import { BlogPostsService } from '~/shared/api/services/blog-posts'
import {
  BlogPostApiEntity,
  BlogPostFormData,
  BlogPostListApiEntity,
  CreateBlogPostPayload,
} from '~/shared/api/services/blog-posts/types'
import { handleAxiosError } from '~/shared/lib'
import { defaultEditorBlocks, EditorBlock, EditorSeoData } from './editor'
import { createInitialEditorSeo, createInitialFormData } from './initial-state'
import { BlogPostsLoaders } from './types'

const cloneEditorBlocks = (blocks: EditorBlock[]) =>
  blocks.map((block) => ({ ...block }))

export class BlogPostsStore {
  posts: BlogPostListApiEntity[] = []

  postById: BlogPostApiEntity | null = null

  isStoreSelectedManually = false

  formData: BlogPostFormData = createInitialFormData()

  editorBlocks: EditorBlock[] = cloneEditorBlocks(defaultEditorBlocks)

  editorSeo: EditorSeoData = createInitialEditorSeo()

  loaders: BlogPostsLoaders = {
    posts: false,
    loadMorePosts: false,
    postById: false,
    createPost: false,
    deletePost: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof BlogPostsLoaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof BlogPostsLoaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFormData = <K extends keyof BlogPostFormData>(
    key: K,
    value: BlogPostFormData[K],
  ) => {
    this.formData[key] = value
  }

  setEditorBlocks = (blocks: EditorBlock[]) => {
    this.editorBlocks = blocks
  }

  setEditorSeo = (value: EditorSeoData) => {
    this.editorSeo = value
  }

  setIsStoreSelectedManually = (value: boolean) => {
    this.isStoreSelectedManually = value
  }

  setEditPostState = (post: BlogPostApiEntity, editorBlocks: EditorBlock[]) => {
    const normalizedTagIds = post.tags.map((tag) =>
      typeof tag === 'string' ? tag : tag.id,
    )
    const normalizedStoreId = post.storeId || post.store?.id || ''
    const normalizedPreviewId = post.previewId ?? post.preview?.id ?? null

    this.postById = post
    this.formData = {
      storeId: normalizedStoreId,
      slug: post.slug || '',
      previewId: normalizedPreviewId,
      publishAt: post.publish_at,
      tags: normalizedTagIds,
      title: post.title || '',
      description: post.description || '',
      content: post.content || '',
      keywords: post.keywords || '',
    }
    this.editorSeo = {
      storeId: normalizedStoreId || null,
      tagIds: normalizedTagIds,
      slug: post.slug || '',
    }
    this.editorBlocks = cloneEditorBlocks(editorBlocks)
    this.isStoreSelectedManually = false
  }

  clearCreatePostForm = () => {
    this.formData = createInitialFormData()
    this.editorBlocks = cloneEditorBlocks(defaultEditorBlocks)
    this.editorSeo = createInitialEditorSeo()
    this.isStoreSelectedManually = false
  }

  getPosts = async (action?: 'loadMorePosts') => {
    if (action === 'loadMorePosts' && this.requestSettings.isAllLoaded) {
      return
    }

    this.activeLoader(action || 'posts')

    try {
      const params = {
        limit: this.requestSettings.limit,
        offset: action === 'loadMorePosts' ? this.requestSettings.offset : 0,
      }

      const { data } = await BlogPostsService.getPosts({ params })

      if (action === 'loadMorePosts') {
        runInAction(() => {
          this.posts = [...this.posts, ...data]
        })
      } else {
        runInAction(() => {
          this.posts = data
        })
      }

      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action === 'loadMorePosts'
              ? this.requestSettings.offset + this.requestSettings.limit
              : this.requestSettings.limit,
        }
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'posts')
    }
  }

  getPostById = async (id: string) => {
    this.activeLoader('postById')

    try {
      const { data } = await BlogPostsService.getPostById(id)

      runInAction(() => {
        this.postById = data
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('postById')
    }
  }

  createPost = async (payload: CreateBlogPostPayload) => {
    this.activeLoader('createPost')

    try {
      const { data } = await BlogPostsService.createPost(payload)

      runInAction(() => {
        this.postById = data
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createPost')
    }
  }

  updatePost = async (id: string, payload: CreateBlogPostPayload) => {
    this.activeLoader('createPost')

    try {
      const { data } = await BlogPostsService.updatePost(id, payload)

      runInAction(() => {
        this.postById = data
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createPost')
    }
  }

  deletePost = async (id: string) => {
    this.activeLoader('deletePost')

    try {
      return await BlogPostsService.deletePost(id)
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('deletePost')
    }
  }
}

export default BlogPostsStore
